/**
 * Reports
 * CSV exports of tenant data. Pick a report type, a date window, and the
 * type's optional filters — the server streams a CSV download.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Stack,
} from '@mui/material';
import { Download, TableView } from '@mui/icons-material';
import DateRangePicker from '@/components/analytics/DateRangePicker';
import adminApi from '@/services/admin.api';

type ReportType = 'leads' | 'conversations' | 'campaigns' | 'appointments' | 'handoffs' | 'ai-chat';

const REPORTS: Array<{
  type: ReportType;
  title: string;
  description: string;
  statusOptions?: string[];
  statusParam?: string;
  extraFilter?: 'channel' | 'source';
}> = [
  {
    type: 'leads',
    title: 'Leads',
    description: 'Every lead with contact details, source, status, interest and tags.',
    statusOptions: ['new', 'contacted', 'qualified', 'converted', 'lost', 'archived'],
    extraFilter: 'source',
  },
  {
    type: 'conversations',
    title: 'Conversations',
    description: 'Chat contacts with channel, status, routing, AI agent and opt-out state.',
    statusOptions: ['active', 'resolved', 'archived'],
    extraFilter: 'channel',
  },
  {
    type: 'campaigns',
    title: 'Campaign Delivery Log',
    description: 'Per-recipient campaign sends with delivery status and errors.',
    statusOptions: ['pending', 'sent', 'delivered', 'failed', 'skipped', 'optOut'],
  },
  {
    type: 'appointments',
    title: 'Appointments',
    description: 'Requested and confirmed appointments with customer details.',
    statusOptions: ['requested', 'confirmed', 'completed', 'cancelled', 'no_show'],
  },
  {
    type: 'handoffs',
    title: 'Human Handoffs',
    description:
      'Every bot→human routing transition: escalations with their trigger, claims, assignments and hand-backs — who acted and when.',
    statusOptions: ['escalated', 'assigned', 'claimed', 'handed_back'],
    statusParam: 'eventType',
  },
  {
    type: 'ai-chat',
    title: 'AI Chat Activity',
    description:
      'Per-message AI activity: which agent answered, answer source (knowledge base / LLM), tokens used, message preview.',
    statusOptions: ['user', 'assistant'],
    statusParam: 'role',
  },
];

const Reports: React.FC = () => {
  const getDefaultRange = () => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  };

  const [dateRange, setDateRange] = useState(getDefaultRange());
  const [statusByType, setStatusByType] = useState<Record<string, string>>({});
  const [extraByType, setExtraByType] = useState<Record<string, string>>({});
  const [downloading, setDownloading] = useState<ReportType | null>(null);
  const [error, setError] = useState('');

  const download = async (type: ReportType) => {
    setError('');
    setDownloading(type);
    try {
      const statusParam = REPORTS.find((r) => r.type === type)?.statusParam || 'status';
      const blob = await adminApi.exportReport(type, {
        startDate: dateRange.start.toISOString(),
        endDate: dateRange.end.toISOString(),
        ...(statusByType[type] ? { [statusParam]: statusByType[type] } : {}),
        ...(extraByType[type]
          ? type === 'conversations'
            ? { channel: extraByType[type] }
            : { source: extraByType[type] }
          : {}),
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${type}-${new Date().toISOString().slice(0, 10)}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e.message || 'Export failed');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight="bold">
          Reports
        </Typography>
        <DateRangePicker
          startDate={dateRange.start}
          endDate={dateRange.end}
          onChange={(start, end) => setDateRange({ start, end })}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {REPORTS.map((report) => (
          <Grid item xs={12} md={6} key={report.type}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <TableView color="primary" />
                  <Typography variant="h6">{report.title}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {report.description}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                  {report.statusOptions && (
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={statusByType[report.type] ?? ''}
                        label="Status"
                        onChange={(e) =>
                          setStatusByType((p) => ({ ...p, [report.type]: e.target.value }))
                        }
                      >
                        <MenuItem value="">All</MenuItem>
                        {report.statusOptions.map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {report.extraFilter === 'channel' && (
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Channel</InputLabel>
                      <Select
                        value={extraByType[report.type] ?? ''}
                        label="Channel"
                        onChange={(e) =>
                          setExtraByType((p) => ({ ...p, [report.type]: e.target.value }))
                        }
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="whatsapp">WhatsApp</MenuItem>
                        <MenuItem value="instagram">Instagram</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={() => download(report.type)}
                    disabled={downloading !== null}
                  >
                    {downloading === report.type ? 'Exporting…' : 'Export CSV'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Reports;
